// Core imports
import GraniteError from 'granite-admin/utils/granite-error';
import GraniteAPIClient from 'granite-admin/utils/granite-client';
import { setOrganisation } from 'granite-admin/utils/auth-singleton';
import axios from 'axios';

// Application imports
import User from 'granite-admin/accounts/entities/user';
import UserPreference from 'granite-admin/accounts/entities/userPreference';
import { env } from 'granite-admin/env';
const url = env.REACT_APP_ACCOUNTS_BASE_URL || process.env.REACT_APP_ACCOUNTS_BASE_URL;
const ssoUrl = env.REACT_APP_SSO_URL || process.env.REACT_APP_SSO_URL;

/**
 *  User Login api call implementation.
 *
 * @param {string} username
 * @param {string} password
 *
 * @returns {string} authorization token if user login was successful.
 * @throws {GraniteError} if user credentials are invalid.
 */

async function login(email, password, organisation_id) {
  const payload = {
    email,
    password
    // ...(organisation_id && { organisation_id }),
  };

  const headers = organisation_id ? {
    organisation: organisation_id
  } : null;
  const response = await GraniteAPIClient(url, headers).post(`/user/login/`, payload);
  if (organisation_id) setOrganisation(organisation_id);
  return response.data;
}
async function ssoLogin(email) {
  return await GraniteAPIClient().get(`${ssoUrl}?email=${email}`);
}
async function ssoLogout(url) {
  try {
    return await GraniteAPIClient().post(url);
  } catch (e) {
    console.log(e.errors);
  }
}
async function adminLogin(email, password) {
  const payload = {
    email,
    password
  };
  await GraniteAPIClient(url).post('', payload);
}

/**
 *  Register api call implementation.
 *
 * @param {Object} user data.
 * @param {string} user.first_name - First name of the user.
 * @param {string} user.last_name - Last name of the user.
 * @param {string} user.password - User password.
 *
 * @returns {string} 'success' as string message.
 * @throws {GraniteError} if user data is invalid.
 */
async function register(values) {
  const payload = {
    ...values
  };
  const response = await GraniteAPIClient(url).post('/user/register/', payload);
  return response;
}

/**
 *  User getProfile api call implementation.
 *
 * @returns {User} user entity
 * @throws {GraniteError} if xhr request fails
 */
async function myProfile() {
  const {
    data
  } = await GraniteAPIClient(url).get('/user/profile/');
  const user = new User(data);
  return user;
}

/**
 *  UpdateProfile api call implementation.
 *
 * @param {string} email
 * @param {string} first_name
 * @param {string} last_name
 * @param {string} username
 *
 * @returns {object} updated profile
 * @throws {GraniteError} if xhr request fails
 */
async function updateMyProfile(first_name, last_name, imageKey) {
  let userData = {};
  if (first_name) userData.first_name = first_name;
  if (last_name) userData.last_name = last_name;
  if (imageKey) userData.profile_image = imageKey;
  const {
    data
  } = await GraniteAPIClient(url).put('/user/me/', userData);
  const user = new User(data);
  return user;
}

/**
 *  request OTP api call implementation.
 *
 * @param {string} email
 *
 * @throws {GraniteError} if xhr request fails
 */
async function requestToken(data) {
  const payload = data;
  await GraniteAPIClient(url).post('/user/recover-password/', payload);
}

/**
 *  submit OTP api call implementation.
 *
 * @param {string} token
 *
 * @returns {string} uid
 * @throws {GraniteError} if xhr request fails
 */
async function submitToken(token, email, phone) {
  const payload = {
    token,
    email,
    phone
  };
  const response = await GraniteAPIClient(url).post('/user/activate/', payload);
  return response.data;
}

/**
 *  Reset password api call implementation.
 *
 * @param {string} uid
 * @param {string} password
 * @param {string} re_password
 *
 * @throws {GraniteError} if xhr request fails
 */
async function resetPassword(jwt_token, new_password, confirm_password) {
  const payload = {
    jwt_token,
    new_password,
    confirm_password
  };
  await GraniteAPIClient(url).post('/user/new-password/', payload);
}

/**
 *  activate user api call implementation.
 *
 * @param {string} uid
 * @param {string} password
 * @param {string} re_password
 *
 * @throws {GraniteError} if xhr request fails
 */
async function activateUser(jwt_token, new_password, confirm_password) {
  const payload = {
    jwt_token,
    new_password,
    confirm_password
  };
  try {
    await GraniteAPIClient(url).post('/user/employee/activate/', payload);
  } catch (error) {
    throw error;
  }
}

/**
 *  User changePhone api call implementation.
 *
 * @param {string} password
 * @param {string} newPhone
 *
 * @throws {GraniteError} if api call fails
 */
async function changePhone(password, new_phone, country_code) {
  const payload = {
    password,
    new_phone,
    country_code
  };
  await GraniteAPIClient(url).post('/user/change-phone/request/', payload);
}

/**
 *  User changeEmail api call implementation.
 *
 * @param {string} password
 * @param {string} newEmail
 *
 * @throws {GraniteError} if api call fails
 */
async function changeEmail(password, new_email) {
  const payload = {
    password,
    new_email
  };
  await GraniteAPIClient(url).post('/user/change/email/request/', payload);
}

/**
 *  User verifyEmailChange api call implementation.
 *
 * @param {string} otp
 *
 * @throws {GraniteError} if api call fails
 */
async function verifyEmailChange(otp) {
  const payload = {
    otp
  };
  await GraniteAPIClient(url).post(`/user/change/email/`, payload);
}

/**
 *  User verifyPhoneChange api call implementation.
 *
 * @param {string} otp
 *
 * @throws {GraniteError} if api call fails
 */
async function verifyPhoneChange(otp) {
  const payload = {
    otp
  };
  await GraniteAPIClient(url).post(`/user/change-phone/`, payload);
}

/**
 *  User ChangePassword api call implementation.
 *
 * @param {string} password
 * @param {string} newPassword
 * @param {string} re_password
 *
 * @throws {GraniteError} if api call fails
 */
async function changePassword(password, new_password, confirm_new_password) {
  const payload = {
    password,
    new_password,
    confirm_new_password
  };
  await GraniteAPIClient(url).post('/user/change-password/', payload);
}
async function logout() {
  try {
    await GraniteAPIClient(url).delete('/user/logout/');
  } catch (e) {
    console.log(e);
  }
}

/**
 *  User get user preference api call implementation.
 *
 * @returns {Preference} preference entity list
 * @throws {GraniteError} if xhr request fails
 */
async function getUserPreferences() {
  try {
    const {
      data
    } = await GraniteAPIClient(url).get('/preference/user-preference/');
    const userPreferences = data ? data.results.map(item => new UserPreference({
      ...item
    })) : [];
    return userPreferences;
  } catch (error) {
    const errorMessage = error.response ? error.response.data.message : error;
    throw new GraniteError(errorMessage);
  }
}

/**
 *  User Change preference api call implementation.
 *
 * @param {int} ID
 * @param {string} value
 *
 * @throws {GraniteError} if api call fails
 */
async function changeUserPreference(ID, value) {
  const payload = {
    value: value
  };
  await GraniteAPIClient(url).put(`/preference/user-preference/${ID}/`, payload);
}
async function getS3Url(file_name) {
  const payload = {
    file_name
  };
  try {
    const response = await GraniteAPIClient(url).post('/user/image/upload/', payload);
    return response.data.result;
  } catch (error) {
    throw error;
  }
}
async function uploadS3(url, fileData) {
  try {
    await axios.post(url, fileData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
  } catch (err) {
    console.log(err);
    throw new GraniteError(err);
  }
}
async function userInvite(values) {
  const payload = {
    email: values.email,
    invitee_name: values.name
  };
  try {
    const data = await GraniteAPIClient(url).post('user/invite/', payload);
  } catch (error) {
    throw error;
  }
}
async function otpSubmit(values) {
  const payload = {
    email: values.email,
    otp: values.otp
  };
  try {
    const response = await GraniteAPIClient(url).post('user/login/otp/submit/', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
}
async function resendOtp(values) {
  const payload = {
    email: values.email,
    phone: values.phone
  };
  try {
    const response = await GraniteAPIClient(url).post('user/resend-activation/', payload);
    return response.data;
  } catch (error) {
    throw error;
  }
}
export default {
  changeEmail,
  changePassword,
  login,
  ssoLogin,
  adminLogin,
  register,
  myProfile,
  resetPassword,
  requestToken,
  submitToken,
  updateMyProfile,
  logout,
  verifyEmailChange,
  changePhone,
  verifyPhoneChange,
  getUserPreferences,
  changeUserPreference,
  getS3Url,
  uploadS3,
  userInvite,
  otpSubmit,
  resendOtp,
  activateUser,
  ssoLogout
};